import { DocumentFileList } from '@/common/components/DocumentFileList'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { checkIsAcceptedFile } from '@/common/utils/fileValidator'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { UploadIcon } from 'lucide-react'
import { FormEvent, forwardRef, useState } from 'react'
import { ArrayPath, Control, FieldValues } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

export interface MultipleDocumentSoftUploadProps<T extends FieldValues> extends React.HTMLAttributes<HTMLInputElement> {
  label?: string
  name?: string
  title?: string
  files: {
    id: string
    attachment_id: string
    content_type: string
    url: string
  }[]
  uploadBlob: (data: { file: File; filename: string }) => Promise<any>
  deletePrivateCompanyLeadGenericDocument: (attachment_id: string) => void
  accept?: string
  className?: string
  control: Control<T>
  fieldName: ArrayPath<T>
  disabled?: boolean
}

export const MultipleDocumentSoftUpload = forwardRef(
  <T extends FieldValues>(
    {
      label,
      name,
      title = 'Anexe um arquivo',
      files,
      accept,
      className,
      uploadBlob,
      deletePrivateCompanyLeadGenericDocument,
      control,
      fieldName,
      disabled,
      ...props
    }: MultipleDocumentSoftUploadProps<T>,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const [serverError, setServerError] = useState('')

    const [dragActive, setDragActive] = useState(false);

    const handleDocumentsChange = async (e: FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement & { files: FileList }

      const files = target.files.length ? Array.from(target.files)?.filter((file) => checkIsAcceptedFile(file, accept || '')) : []

      if (!files.length) {
        return
      }

      for (const file of files) {
        const data = await uploadBlob({ file, filename: file.name })

        if (data?.data?.data?.id) {
          setServerError('')
        }
      }
      target.value = ''
    }

    const handleRemoveFile = (currentIndex: number) => {
      if (files.length > 0) {
        deletePrivateCompanyLeadGenericDocument(files[currentIndex].attachment_id)
      }
    }

    return (
      <div className="space-y-2">
        {serverError && <ErrorMessage name={name} message={serverError} />}
        <div className="flex flex-wrap gap-4">
          {!!files.length && (
            <DocumentFileList
              files={
                files?.map((document) => ({
                  id: document.attachment_id,
                  fileName: getFilenameFromUrl(document?.url),
                  currentFileUrl: document?.url,
                })) || []
              }
              handleRemoveFile={handleRemoveFile}
              disableRemoveFile={disabled}
              className={className}
            />
          )}
          <div className="flex flex-col items-start">
            {label && <p className="text-xs mb-1">{label}</p>}
            <label
              className={twMerge(
                'flex flex-col justify-center border border-neutral-3 w-[180px] h-[100px] gap-2 items-center bg-neutral-1 cursor-pointer rounded-lg',
                disabled && 'opacity-50 cursor-default',
                className,
                dragActive && "bg-blue-100/30 border-blue-300",
              )}
              htmlFor={fieldName as string}
              onDragLeave={e => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragActive(false);
                }
              }}
              onDragEnter={e => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragOver={e => {
                e.preventDefault();
              }}
              onDrop={e => {
                e.preventDefault();
                setDragActive(false);
                handleDocumentsChange({ target: { files: e.dataTransfer.files } } as any);
              }}
            >
              <p className="text-xs text-center">{title}</p>
              <UploadIcon strokeWidth={1} />
              <p className="text-xs italic">{dragActive ? "Solte aqui" : "Ou arraste até aqui"}</p>
            </label>

            <input
              type="file"
              ref={ref}
              name={name}
              className="hidden"
              id={fieldName as string}
              accept={accept}
              multiple
              onChange={handleDocumentsChange}
              disabled={disabled}
              {...props}
            />
          </div>
        </div>
      </div>
    )
  },
)
